import React, { useMemo, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const PAGE_TITLES = {
    '/': 'Accueil',
    '/a-propos': 'A propos',
    '/assurances': 'Nos assurances',
    '/contact': 'Contact',
    '/connexion': 'Connexion',
    '/espace-client': 'Tableau de bord',
    '/espace-client/contrats': 'Mes contrats',
    '/espace-client/sinistres': 'Mes sinistres',
    '/espace-client/sinistres/declarer': 'Declarer un sinistre',
    '/espace-client/situation': 'Situation financiere',
    '/espace-client/profil': 'Mon profil',
};

const ASSURANCE_TITLES = {
    auto: 'Assurance Automobile',
    habitation: 'Assurance Habitation',
    sante: 'Assurance Sante',
    chantier: 'Assurance Chantier',
    transport: 'Assurance Transport',
    'responsabilite-civile': 'Responsabilite Civile',
    plantation: 'Assurance Plantation',
    recolte: 'Assurance Recolte',
    betail: 'Assurance Betail',
};

function ScrollToTop() {
    const { pathname } = useLocation();
    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' });

        const assuranceMatch = pathname.match(/^\/assurances\/(.+)$/);
        let pageTitle = PAGE_TITLES[pathname];
        if (assuranceMatch) {
            pageTitle = ASSURANCE_TITLES[assuranceMatch[1]] || 'Nos assurances';
        }
        document.title = pageTitle
            ? `CAP Assurances | ${pageTitle}`
            : 'CAP Assurances - Societe de Courtage';
    }, [pathname]);
    return null;
}

import { AppThemeProvider } from './theme/AppThemeProvider';
import { ClientAuthContext } from './common/constants';
import UseClientAuth from './common/hooks/UseClientAuth';
import ProtectedRoute from './components/protectedRoute';

// Layouts
import PublicLayout from './pages/public/PublicLayout';
import ClientLayout from './pages/client/ClientLayout';

// Pages publiques
import Accueil from './pages/public/accueil/Accueil';
import APropos from './pages/public/a-propos/APropos';
import Assurances from './pages/public/assurances/Assurances';
import Contact from './pages/public/contact/Contact';
import Connexion from './pages/public/connexion/Connexion';

// Pages espace client
import ClientDashboard from './pages/client/dashboard/Dashboard';
import MesContrats from './pages/client/contrats/MesContrats';
import DetailContrat from './pages/client/contrats/DetailContrat';
import MesSinistres from './pages/client/sinistres/MesSinistres';
import DeclarerSinistre from './pages/client/sinistres/DeclarerSinistre';
import Situation from './pages/client/situation/Situation';
import ClientProfile from './pages/client/profile/Profile';

import './App.scss';

function App() {
    const auth = UseClientAuth();

    const authValue = useMemo(() => ({
        currentClient: auth.currentClient,
        setCurrentClient: auth.setCurrentClient,
        onLogin: auth.onLogin,
        onLogout: auth.onLogout,
        isAuthenticated: auth.isAuthenticated,
        initialized: auth.initialized,
    }), [auth.currentClient, auth.isAuthenticated, auth.initialized, auth.onLogin, auth.onLogout, auth.setCurrentClient]);

    return (
        <AppThemeProvider>
            <ClientAuthContext.Provider value={authValue}>
                <BrowserRouter>
                    <ScrollToTop />
                    <Routes>
                        {/* Pages publiques avec Navbar + Footer */}
                        <Route path="/" element={<PublicLayout />}>
                            <Route index element={<Accueil />} />
                            <Route path="a-propos" element={<APropos />} />
                            <Route path="assurances" element={<Assurances />} />
                            <Route path="assurances/:type" element={<Assurances />} />
                            <Route path="contact" element={<Contact />} />
                            <Route path="connexion" element={<Connexion />} />
                        </Route>

                        {/* Espace client (protege, avec sidebar) */}
                        <Route
                            path="/espace-client"
                            element={
                                <ProtectedRoute>
                                    <ClientLayout />
                                </ProtectedRoute>
                            }
                        >
                            <Route index element={<ClientDashboard />} />
                            <Route path="contrats" element={<MesContrats />} />
                            <Route path="contrats/:id" element={<DetailContrat />} />
                            <Route path="sinistres" element={<MesSinistres />} />
                            <Route path="sinistres/declarer" element={<DeclarerSinistre />} />
                            <Route path="situation" element={<Situation />} />
                            <Route path="profil" element={<ClientProfile />} />
                        </Route>

                        {/* Catch-all */}
                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                </BrowserRouter>

                <ToastContainer
                    position="top-right"
                    autoClose={4000}
                    hideProgressBar={false}
                    closeOnClick
                    pauseOnHover
                    theme="colored"
                />
            </ClientAuthContext.Provider>
        </AppThemeProvider>
    );
}

export default App;
